import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { LessonLog, TestRecord } from '@/types';
import LessonCardItem from '@/pages/lessons/components/LessonCardItem';
import { markProgressPlanned } from '@/lib/progress';
import LessonCalendar, { lessonDateKey, type MonthCursor } from '@/components/feature/LessonCalendar';

interface LessonLogSectionProps {
  studentId: string;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function formatSelectedDate(dateKey: string): string {
  const d = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${WEEKDAYS[d.getDay()]})`;
}

export default function LessonLogSection({ studentId }: LessonLogSectionProps) {
  const [lessons, setLessons] = useState<LessonLog[]>([]);
  const [testsByLesson, setTestsByLesson] = useState<Record<string, TestRecord[]>>({});
  const [progressMap, setProgressMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [cursor, setCursor] = useState<MonthCursor>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const didInit = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('lesson_logs')
        .select('*')
        .eq('student_id', studentId)
        .order('lesson_date', { ascending: false });
      if (fetchError) throw fetchError;
      const ls = (data as LessonLog[]) ?? [];
      setLessons(ls);

      const ids = ls.map((l) => l.id);
      if (ids.length > 0) {
        const { data: tr, error: testError } = await supabase
          .from('test_records')
          .select('*')
          .in('lesson_log_id', ids);
        if (testError) throw testError;
        const map: Record<string, TestRecord[]> = {};
        ((tr as TestRecord[]) ?? []).forEach((t) => {
          if (!t.lesson_log_id) return;
          if (!map[t.lesson_log_id]) map[t.lesson_log_id] = [];
          map[t.lesson_log_id].push(t);
        });
        setTestsByLesson(map);
      } else {
        setTestsByLesson({});
      }

      const { data: pi } = await supabase
        .from('progress_items')
        .select('id, unit')
        .eq('student_id', studentId);
      const pmap: Record<string, string> = {};
      ((pi as { id: string; unit: string }[]) ?? []).forEach((p) => {
        pmap[p.id] = p.unit;
      });
      setProgressMap(pmap);
      if (!didInit.current && ls.length > 0) {
        const key = lessonDateKey(ls[0].lesson_date);
        setSelectedDate(key);
        const d = new Date(ls[0].lesson_date);
        if (!Number.isNaN(d.getTime())) {
          setCursor({ year: d.getFullYear(), month: d.getMonth() });
        }
        didInit.current = true;
      }
    } catch {
      setError('수업일지를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id: string) => {
    try {
      const target = lessons.find((l) => l.id === id);
      await supabase.from('test_records').delete().eq('lesson_log_id', id);
      const { error: deleteError } = await supabase.from('lesson_logs').delete().eq('id', id);
      if (deleteError) throw deleteError;
      await markProgressPlanned(target?.progress_item_ids ?? []);
      setLessons((prev) => prev.filter((l) => l.id !== id));
    } catch {
      setError('삭제에 실패했어요.');
    }
  };

  const selectedLessons = selectedDate
    ? lessons.filter((l) => lessonDateKey(l.lesson_date) === selectedDate)
    : [];

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-book-2-line"></i>
            </span>
            수업일지
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            달력에서 수업한 날을 누르면 그날의 수업일지를 볼 수 있어요. 총{' '}
            <strong className="font-semibold text-foreground-700">{lessons.length}</strong>건
          </p>
        </div>
        <Link
          to={`/lessons/new?student=${studentId}`}
          className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          수업일지 작성
        </Link>
      </div>

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-sm text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin"></i>
          불러오는 중...
        </div>
      ) : lessons.length === 0 ? (
        <p className="mt-5 rounded-lg border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
          아직 기록된 수업일지가 없어요.
        </p>
      ) : (
        <div className="mt-5">
          <LessonCalendar
            lessons={lessons}
            cursor={cursor}
            onCursorChange={setCursor}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
          />

          <div className="mt-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-calendar-check-line text-primary-600"></i>
                {selectedDate ? formatSelectedDate(selectedDate) : '날짜를 선택해주세요'}
              </h3>
              {selectedLessons.length > 0 && (
                <span className="text-xs text-foreground-500">
                  수업일지 <strong className="font-semibold text-foreground-700">{selectedLessons.length}</strong>건
                </span>
              )}
            </div>

            {selectedLessons.length === 0 ? (
              <p className="mt-3 rounded-lg border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
                선택한 날짜에는 수업일지가 없어요.
                <br className="hidden sm:block" /> 달력에서 색이 표시된 날을 눌러보세요.
              </p>
            ) : (
              <div className="mt-3 flex flex-col gap-3">
                {selectedLessons.map((l) => (
                  <LessonCardItem
                    key={l.id}
                    lesson={l}
                    tests={testsByLesson[l.id] ?? []}
                    progressUnits={(l.progress_item_ids ?? [])
                      .map((pid) => progressMap[pid])
                      .filter(Boolean)}
                    to={`/lessons/${l.id}/edit`}
                    onDelete={remove}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}